'use client'

import { useState } from 'react'
import { Band, Section } from '@/components/site/Band'
import { track } from '@/lib/analytics'
import type { Question } from '@/lib/portfolio'

/**
 * The question list every page closes on.
 *
 * One open item at a time, which is the design's behaviour and also the reason
 * this is stateful at all: a `<details>` element would be cheaper and would
 * let a reader open all five, losing the sense that these are alternatives to
 * scan rather than a document to read.
 */
export function Faq({ heading, items }: { heading: string; items: Question[] }) {
    const [open, setOpen] = useState<number | null>(null)

    if (items.length === 0) {
        return null
    }

    return (
        <Section edge="top">
            <Band>
                <div className="px-gutter pt-28 pb-10">
                    <h2
                        data-wave
                        className="m-0 max-w-[24ch] text-[clamp(26px,2.8vw,38px)] leading-[1.18] font-normal tracking-[-0.035em] text-balance"
                    >
                        {heading}
                    </h2>
                </div>
                <div className="border-t border-hair">
                    {items.map((item, index) => {
                        const isOpen = open === index

                        return (
                            <div key={item.question} className="border-b border-hair">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setOpen(isOpen ? null : index)

                                        // Closing one is not a question asked.
                                        if (!isOpen) {
                                            track({
                                                event: 'faq_open',
                                                faq_question: item.question,
                                            })
                                        }
                                    }}
                                    aria-expanded={isOpen}
                                    className="flex w-full cursor-pointer items-baseline gap-6 border-0 bg-transparent p-gutter text-left font-sans text-fg transition-[padding-left,background] duration-500 ease-design hover:bg-hover hover:pl-[46px]"
                                >
                                    <span className="flex-1 text-[19px] tracking-[-0.02em]">
                                        {item.question}
                                    </span>
                                    <span className="font-mono text-sm text-faint">{isOpen ? '−' : '+'}</span>
                                </button>
                                {isOpen ? (
                                    <p className="m-0 max-w-[62ch] animate-rise-in px-gutter pb-7 text-base leading-[1.75] text-pretty text-dim">
                                        {item.answer}
                                    </p>
                                ) : null}
                            </div>
                        )
                    })}
                </div>
            </Band>
        </Section>
    )
}
